'use client';
import { useEffect, useState } from 'react';
import { hubUrl } from '../../../hub-url.js';
import { callViaProxy } from '../hooks/call-via-proxy.js';
// Module-level cache so multiple <TrackframeHeader /> mounts on the same page
// don't refetch the same data. Keyed on the proxy mount-point so an app that
// embeds two headers across two hubs (uncommon, but cheap to support) doesn't
// crosstalk.
const cache = new Map();
function cacheKey(opts) {
    return `${opts.hubProxyBaseUrl ?? '__direct__'}|${opts.hubBaseUrl ?? '__same__'}`;
}
async function callDirect(hubBaseUrl, path, getAuthHeaders) {
    const auth = (await getAuthHeaders?.()) ?? {};
    const url = hubUrl(hubBaseUrl, path);
    const res = await fetch(url, {
        credentials: hubBaseUrl ? 'include' : undefined,
        headers: { ...auth },
    });
    if (!res.ok) {
        throw new Error(`${path} failed: ${res.status} ${res.statusText}`);
    }
    return (await res.json());
}
async function loadHeaderData(options) {
    // Tracks resolution is best-effort — a 403 on /tracks (e.g. no read perm)
    // shouldn't blank the whole header. Identity + branding remain hard
    // dependencies; if either fails, the header surfaces the error instead.
    const tracksPromise = options.hubProxyBaseUrl
        ? callViaProxy(options.hubProxyBaseUrl, 'tracksList', options.getAuthHeaders).catch(() => [])
        : callDirect(options.hubBaseUrl, '/tracks', options.getAuthHeaders).catch(() => []);
    if (options.hubProxyBaseUrl) {
        const [me, branding, tracks] = await Promise.all([
            callViaProxy(options.hubProxyBaseUrl, 'authMe', options.getAuthHeaders),
            callViaProxy(options.hubProxyBaseUrl, 'settingsBranding', options.getAuthHeaders),
            tracksPromise,
        ]);
        return { identity: me.user, branding, tracks };
    }
    const [me, branding, tracks] = await Promise.all([
        callDirect(options.hubBaseUrl, '/auth/me', options.getAuthHeaders),
        callDirect(options.hubBaseUrl, '/settings/branding', options.getAuthHeaders),
        tracksPromise,
    ]);
    return { identity: me.user, branding, tracks };
}
/**
 * Fetches identity (`/auth/me`) and branding (`/settings/branding`) for the
 * shared header. Same-origin by default; pass `hubProxyBaseUrl` when the
 * track routes calls through its hub-proxy so the hub API key never enters
 * the browser.
 */
export function useHeaderData(options = {}) {
    const [identity, setIdentity] = useState(null);
    const [branding, setBranding] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // Stable cache key — the options object itself is rarely memoized by
    // callers. We only refetch when the proxy URL itself changes.
    const key = cacheKey(options);
    useEffect(() => {
        let cancelled = false;
        let promise = cache.get(key);
        if (!promise) {
            promise = loadHeaderData(options);
            cache.set(key, promise);
        }
        promise
            .then((data) => {
            if (cancelled)
                return;
            setIdentity(data.identity);
            setBranding(data.branding);
            setTracks(data.tracks);
            setLoading(false);
        })
            .catch((err) => {
            if (cancelled)
                return;
            // Drop the failed promise so the next mount retries instead of
            // memoizing the error forever.
            cache.delete(key);
            setError(err instanceof Error ? err : new Error(String(err)));
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- key already encodes the relevant options
    }, [key]);
    return { identity, branding, tracks, loading, error };
}
//# sourceMappingURL=use-header-data.js.map